// Pure, total admin-order-search decider.
//
// `planAdminOrderSearch` maps one (criterion, term) pair to exactly one plan —
// an executed search carrying that criterion's column and the `%term%` ILIKE
// pattern, or a refusal naming the three-character minimum. It is the single
// place that decides execute-vs-refuse for the admin orders list search: the
// route answers 400 from the refusal it returns and never re-derives the
// minimum, and the portal's criterion dropdown reads the same registry, so
// both consumers check against one source.
//
// Pure function — no side effects, no I/O, no throws. Determined solely by the
// (criterion, term) pair, so the same inputs always yield the same plan.
/** The minimum term length an executed search accepts. */
export const ADMIN_ORDER_SEARCH_MIN_TERM_LENGTH = 3;
/** The refusal hint for a sub-minimum term — names the three-character minimum. */
export const ADMIN_ORDER_SEARCH_MIN_TERM_HINT = 'Search term must be at least three characters.';
/**
 * The six operator-searchable criteria, in dropdown order: four orders columns
 * and two joined accounts columns. This tuple is the wire vocabulary — the
 * route's `search_field` enum derives from it and the portal dropdown lists
 * it — so a criterion added or removed here moves both consumers together.
 */
export const ADMIN_ORDER_SEARCH_CRITERION_IDS = [
    'orderNumber',
    'caseNumber',
    'caseTitle',
    'internalReferenceNumber',
    'accountName',
    'accountNumber',
];
// Keyed by the closed criterion union, so a criterion without an entry is a
// compile error and the decider's lookup is total — no absent branch to guess
// a fallback for.
const CRITERION_BY_ID = {
    orderNumber: { id: 'orderNumber', label: 'Order #', table: 'orders', column: 'order_number' },
    caseNumber: { id: 'caseNumber', label: 'Case #', table: 'orders', column: 'case_number' },
    caseTitle: { id: 'caseTitle', label: 'Case Title', table: 'orders', column: 'case_title' },
    internalReferenceNumber: {
        id: 'internalReferenceNumber',
        label: 'Internal Reference #',
        table: 'orders',
        column: 'internal_reference_number',
    },
    accountName: { id: 'accountName', label: 'Account Name', table: 'accounts', column: 'name' },
    accountNumber: { id: 'accountNumber', label: 'Account Number', table: 'accounts', column: 'account_number' },
};
/** The six criteria, in dropdown order. */
export const ADMIN_ORDER_SEARCH_CRITERIA = [
    CRITERION_BY_ID.orderNumber,
    CRITERION_BY_ID.caseNumber,
    CRITERION_BY_ID.caseTitle,
    CRITERION_BY_ID.internalReferenceNumber,
    CRITERION_BY_ID.accountName,
    CRITERION_BY_ID.accountNumber,
];
/**
 * Decide how one (criterion, term) admin orders search executes. A term
 * shorter than the three-character minimum is refused with a hint naming the
 * minimum (a pattern with no extractable trigram would force a full scan);
 * any other term executes, scoped to the criterion's column with the
 * `%${term}%` pattern. The term is measured as received — a caller that wants
 * trimmed semantics trims before calling.
 */
export function planAdminOrderSearch(criterion, term) {
    if (term.length < ADMIN_ORDER_SEARCH_MIN_TERM_LENGTH) {
        return { outcome: 'refused', hint: ADMIN_ORDER_SEARCH_MIN_TERM_HINT };
    }
    const entry = CRITERION_BY_ID[criterion];
    return { outcome: 'executed', criterion: entry.id, column: entry.column, pattern: `%${term}%` };
}
